import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { ResolveBlockForm } from '@bechtel/client-blocks';
import { ErrorBoundary, TitleValue } from '@bechtel/client-common';
import { GET_WELD_LIST_BY_IDS } from '@bechtel/client-gql';
import { ClaimReclaimWeld, WeldSendBack } from '@bechtel/client-weld-common';
import {
  Box,
  Button,
  Icon,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetHeader,
  SideSheetSize,
  SideSheetSubHeader,
  Spinner,
  SpinnerSize,
  Text,
} from '@bechtel/gds';
import { CommodityMilestone, CommodityStatusCode, CommodityWrapper, MilestoneStatusCode, Weld } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { map, some } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { BulkWeldAssign } from '~/components/organisms/assign-reassign-weld/bulk';
import { useWorkPackage } from '~/detail';
import { useCommodity } from '~/detail/commodities/context';
import { Reclaim } from './reclaim';
import { TabButtonStyled, WeldFlangeSideSheetContentColStyled, WeldFlangeTabStyled } from './styled';
import { WeldFlageConnectionProps, WeldFlangeTab, WeldFlangeTabType } from './types';
import { WeldDetail } from './weld-detail';
import Welds from './welds';

export const WeldFlangeTabList: WeldFlangeTab[] = [
  { label: t`Welds`, value: WeldFlangeTabType.WELDS },
  // { label: t`Flange Connections`, value: WeldFlangeTabType.FLANGE_CONNECTIONS },
];

interface SidesheetContentProps {
  weldDetails: Weld;
  milestoneData: CommodityMilestone;
  setMilestoneData: (milestone: CommodityMilestone | null) => void;
  showItrCertDialog?: boolean;
}

const SidesheetContent: FC<SidesheetContentProps> = ({
  weldDetails,
  milestoneData,
  setMilestoneData,
  showItrCertDialog,
}) => {
  const { id, assignedCrewMembers, facilities } = useWorkPackage();

  if (
    (weldDetails?.status?.status === CommodityStatusCode.VT_SENT_BACK &&
      milestoneData?.currentStatus?.codeI18n == MilestoneStatusCode.REJECTED) ||
    (weldDetails?.status?.status === CommodityStatusCode.READY_TO_WELD &&
      [MilestoneStatusCode.UNPLANNED, MilestoneStatusCode.PLANNED].includes(milestoneData?.currentStatus?.codeI18n))
  ) {
    return (
      <ErrorBoundary>
        <Box flexDirection={{ sm: 'column' }} px={{ sm: '500' }} py={{ sm: '450' }}>
          <Text size="400" weight="600">
            {milestoneData?.currentStatus?.codeI18n == MilestoneStatusCode.REJECTED ? t`Reclaim` : t`Claim`}
          </Text>
          <Box mt={{ sm: '300' }} alignItems={{ sm: 'center' }}>
            <Text size="400" weight="600">
              <Trans>Milestone</Trans>
            </Text>
            <Text size="400" weight="400">
              {`: ${milestoneData?.name}`}
            </Text>
          </Box>
        </Box>
        <ClaimReclaimWeld
          weldCommodity={weldDetails}
          isReclaim={milestoneData?.currentStatus?.codeI18n == MilestoneStatusCode.REJECTED}
          close={() => setMilestoneData(null)}
          refetchQueries={['commodityStatsSummary']}
        />
      </ErrorBoundary>
    );
  }

  if (milestoneData?.currentStatus?.codeI18n == MilestoneStatusCode.BLOCKED) {
    return (
      <>
        <Box
          p={{ sm: '500' }}
          flexDirection={{ sm: 'column' }}
          gap={{ sm: '200' }}
          css={`
            ${({ theme }) => `background-color: ${theme.colors.red[100]};`}
          `}
        >
          <Text size="400" color="grey.1000" weight="600">
            <Trans>Resolve Block</Trans>
          </Text>
          <Text size="300" color="grey.1000">
            {weldDetails.tag}
          </Text>
        </Box>
        <ErrorBoundary>
          <ResolveBlockForm
            block={milestoneData.blocks[0]} // TODO : no multi block UI / to support multi block in future
            workPackageId={id}
            assignedCrewMembers={assignedCrewMembers}
            weldCommodityAssignees={weldDetails.commodityAssignees}
            facilities={facilities}
            onClose={() => setMilestoneData(null)}
          />
        </ErrorBoundary>
      </>
    );
  }

  if (
    weldDetails?.status?.status === CommodityStatusCode.FITUP_INSPECTION ||
    weldDetails?.status?.status === CommodityStatusCode.CLAIMED
  ) {
    return (
      <>
        <Box flexDirection={{ sm: 'column' }} px={{ sm: '500' }} py={{ sm: '450' }}>
          <Text size="400" weight="600">
            {weldDetails?.status?.status == CommodityStatusCode.FITUP_INSPECTION
              ? t`Send Back to Piping`
              : t`Send Back to Foreman`}
          </Text>
          <Box mt={{ sm: '300' }} alignItems={{ sm: 'center' }}>
            <Text size="400" weight="600">
              <Trans>Milestone</Trans>
            </Text>
            <Text size="400" weight="400">
              {`: ${milestoneData?.name}`}
            </Text>
          </Box>
        </Box>
        <WeldSendBack
          currentMilestone={milestoneData}
          onClose={() => {
            setMilestoneData(null);
          }}
          refetchQueries={['commodityStatsSummary']}
        />
      </>
    );
  }

  if (weldDetails?.status?.status === CommodityStatusCode.SENT_BACK_TO_PIPING) {
    return (
      <ErrorBoundary>
        <Reclaim
          milestone={milestoneData}
          closeModal={() => setMilestoneData(null)}
          refetchQueries={['commodityStatsSummary']}
          showItrCertDialog={showItrCertDialog}
        />
      </ErrorBoundary>
    );
  }

  return null;
};

const WeldFlangeConnections: FC<WeldFlageConnectionProps> = ({ close, weldIds, itrRequired, isUnitOfMeasureHour }) => {
  const { typeName, tag: commodityTag } = useCommodity();
  const [selectedWeldIds, setSelectedWeldIds] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState<WeldFlangeTabType>(WeldFlangeTabType.WELDS);
  const [isFitupRequired, setIsFitupRequired] = useState<boolean>(true);
  const [milestoneData, setMilestoneData] = useState<CommodityMilestone | null>(null);
  const [showItrCertDialog, setShowItrCertDialog] = useState<boolean>(false);
  const { i18n } = useLingui();
  const [weldDetails, setWeldDetails] = useState<Weld>();

  const [loadWeldList, { loading, error, data, refetch, networkStatus }] = useLazyQuery<
    { commoditiesByIds: CommodityWrapper[] },
    {
      ids: Weld['id'][];
    }
  >(GET_WELD_LIST_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (selectedWeldIds.length === 0) return;
    setWeldDetails(undefined);
  }, [selectedWeldIds]);

  useEffect(() => {
    if (!weldDetails) return;
    setSelectedWeldIds([]);
    setMilestoneData(null);
  }, [weldDetails]);

  useEffect(() => {
    if (!weldIds || weldIds.length === 0) return;
    loadWeldList({
      variables: {
        ids: weldIds,
      },
    });
  }, []);

  const updateITRCertDialogValue = () => {
    // if we have any itr required as true we dont need to show the certificate,
    //so we will show the certificate only if both the flags are true i.e. itrRequired and isFreshCertificateRequired which is derived from itrRequired under weld
    const isFreshCertificateRequired =
      !some(welds, (weld) => {
        return weld.itrRequired;
      }) && itrRequired;
    setShowItrCertDialog(isFreshCertificateRequired);
  };

  const welds: Weld[] = data?.commoditiesByIds ? map(data.commoditiesByIds, 'weld') : [];

  useEffect(() => {
    //return if welds are null or empty or itr flag is false
    if (itrRequired === false && !welds && welds?.length == 0) return;
    updateITRCertDialogValue();
  }, [welds]);

  return (
    <>
      <SideSheet
        onDismiss={close}
        size={milestoneData?.id && selectedWeldIds.length === 0 ? SideSheetSize.LARGE : SideSheetSize.MEDIUM}
        visible
      >
        <SideSheetHeader title={t`Welds & Flange Connections`} data-testid="sidesheet-heading">
          <SideSheetClose onClick={close}></SideSheetClose>
        </SideSheetHeader>
        <SideSheetBody style={{ overflow: 'hidden' }}>
          <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} flexBasis={{ sm: '67%' }}>
            <SideSheetSubHeader data-testid="sidesheet-subheading">
              <TitleValue title={typeName} value={commodityTag} size="500" />
            </SideSheetSubHeader>
            <Box flexGrow={{ sm: 1 }} css={{ height: 'calc(100% - 60px)' }}>
              <WeldFlangeSideSheetContentColStyled style={{ flexBasis: '45%' }}>
                {(networkStatus === NetworkStatus.refetch || loading) && (
                  <Box
                    p={{ sm: '500' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent={{ sm: 'center' }}
                    flexGrow={{ sm: 1 }}
                    css={{ height: '100%' }}
                    data-testid="weld-list-loading"
                  >
                    <Spinner size={SpinnerSize.LARGE} />
                  </Box>
                )}
                {!loading && error && (
                  <Box
                    alignItems={{ sm: 'center' }}
                    justifyContent={{ sm: 'center' }}
                    flexGrow={{ sm: 1 }}
                    flexDirection={{ sm: 'column' }}
                    p={{ sm: '500' }}
                    data-testid="welds-error"
                  >
                    <Box pb={{ sm: '500' }} css={{ textAlign: 'center' }}>
                      <Text>
                        <Trans>There was a problem loading Welds. Please click the button below to try again.</Trans>
                      </Text>
                    </Box>
                    <Button onClick={() => refetch()} css={{ width: 'auto' }}>
                      <Box mr={{ sm: '400' }}>
                        <Icon icon="undo" />
                      </Box>
                      <Trans>Retry</Trans>
                    </Button>
                  </Box>
                )}
                {!loading && !error && (
                  <>
                    <WeldFlangeTabStyled data-testid="weld-flange-tabs">
                      {map(WeldFlangeTabList, (tabItem: WeldFlangeTab) => {
                        return (
                          <TabButtonStyled
                            selected={tabItem.value === selectedTab}
                            onClick={() => {
                              setSelectedTab(tabItem.value);
                            }}
                            key={tabItem.value}
                            disabled={tabItem.value == WeldFlangeTabType.FLANGE_CONNECTIONS}
                            data-testid="weld-flange-tab"
                          >
                            {i18n._(tabItem.label)} ({tabItem.value == WeldFlangeTabType.WELDS ? weldIds.length : 0})
                          </TabButtonStyled>
                        );
                      })}
                    </WeldFlangeTabStyled>
                    {selectedTab == WeldFlangeTabType.WELDS && (
                      <ErrorBoundary>
                        <Welds
                          weldList={welds}
                          selectedWeldIds={selectedWeldIds}
                          setSelectedWeldIds={setSelectedWeldIds}
                          setSelectedWeld={(weld) => {
                            setSelectedWeldIds([]);
                            setWeldDetails(weld);
                          }}
                          selectedWeld={weldDetails}
                          isFitupRequired={isFitupRequired}
                        />
                      </ErrorBoundary>
                    )}
                  </>
                )}
              </WeldFlangeSideSheetContentColStyled>
              <WeldFlangeSideSheetContentColStyled style={{ flexBasis: '55%' }}>
                {selectedWeldIds.length === 0 && (
                  <WeldDetail
                    weldId={weldDetails?.id}
                    showItrCertDialog={showItrCertDialog}
                    isFitupRequired={isFitupRequired}
                    setIsFitupRequired={setIsFitupRequired}
                    setMilestoneData={setMilestoneData}
                    close={close}
                    isUnitOfMeasureHour={isUnitOfMeasureHour}
                  />
                )}
                {selectedWeldIds.length > 0 && (
                  <BulkWeldAssign
                    showItrCertDialog={showItrCertDialog}
                    isFitupRequired={isFitupRequired}
                    setIsFitupRequired={setIsFitupRequired}
                    selectedWeldIds={selectedWeldIds}
                    close={close}
                  />
                )}
              </WeldFlangeSideSheetContentColStyled>
            </Box>
          </Box>
          {weldDetails && milestoneData?.id && selectedWeldIds.length === 0 && (
            <WeldFlangeSideSheetContentColStyled data-testid="reclaim-container">
              <SidesheetContent
                weldDetails={weldDetails}
                milestoneData={milestoneData}
                setMilestoneData={setMilestoneData}
                showItrCertDialog={showItrCertDialog}
              />
            </WeldFlangeSideSheetContentColStyled>
          )}
        </SideSheetBody>
      </SideSheet>
    </>
  );
};

export default WeldFlangeConnections;
